import { createFileRoute, useRouter } from "@tanstack/react-router";
import { loadSettings, updateSettingsFn } from "@/lib/server-fns/settings";
import {
  Container,
  Heading,
  Button,
  Card,
  VStack,
  HStack,
  Field,
  SegmentGroup,
  For,
} from "@chakra-ui/react";
import { showErrorToast } from "@/lib/toast-utils";
import { toaster } from "@/components/ui/toaster";
import { CURRENCY_ICONS, SUPPORTED_CURRENCIES } from "@/lib/constants";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/settings")({
  loader: () => loadSettings(),
  component: Settings,
});

function Settings() {
  const { defaultCurrency } = Route.useLoaderData();
  const router = useRouter();
  const [currency, setCurrency] = useState(defaultCurrency);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettingsFn({ data: { defaultCurrency: currency } });
      toaster.create({
        title: "Settings saved",
        type: "success",
        closable: true,
      });
      router.invalidate();
    } catch (error) {
      showErrorToast("Failed to save settings", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxW="md" px={{ base: "3", md: "4" }}>
      <Heading as="h1" size="xl" mb="6" px={{ base: "1", md: "0" }}>
        Settings
      </Heading>

      <Card.Root variant="outline">
        <Card.Body p={{ base: "4", md: "6" }}>
          <VStack gap="6" align="stretch">
            <Field.Root>
              <Field.Label>Default Currency</Field.Label>
              <Field.HelperText>
                Used as the default when creating new expenses and settlements
              </Field.HelperText>
              <SegmentGroup.Root
                value={currency}
                onValueChange={(details) => setCurrency(details.value ?? defaultCurrency)}
                width="full"
                css={{
                  "--segment-indicator-bg": "colors.loonie.solid",
                  "--segment-indicator-shadow": "shadows.md",
                }}
              >
                <SegmentGroup.Indicator />
                <For each={SUPPORTED_CURRENCIES}>
                  {(code) => (
                    <SegmentGroup.Item key={code} value={code}>
                      <SegmentGroup.ItemText>
                        <HStack gap="1.5">
                          {CURRENCY_ICONS[code]}
                          {code}
                        </HStack>
                      </SegmentGroup.ItemText>
                      <SegmentGroup.ItemHiddenInput />
                    </SegmentGroup.Item>
                  )}
                </For>
              </SegmentGroup.Root>
            </Field.Root>

            <Button onClick={handleSave} loading={saving} alignSelf="flex-end">
              Save Settings
            </Button>
          </VStack>
        </Card.Body>
      </Card.Root>
    </Container>
  );
}
